import { IFormContext } from "@multiweb/types/multiweb";
import { IconCode } from "./iconCode";
import { IResultMessage } from "./responses";
/**
 * Datová struktura položek pro hlavní menu
 * @includeToDoc
 */
export interface IMenuData {
    /** Položky hlavního menu */
    readonly items: Readonly<MenuItemsCollection>;

    /** Identifikátory kořenových formulářových položek menu */
    readonly rootFormItemIds: ReadonlyArray<number>;

    /** Profily formulářů */
    readonly profiles: Readonly<MenuProfilesCollection>;
}

/**
 * Kolekce s položkami hlavního menu
 * @includeToDoc
 */
export type MenuItemsCollection = { [id: number]: IMenuItemData };

/**
 * Kolekce s profily formulářů pro hlavního menu
 * @includeToDoc
 */
export type MenuProfilesCollection = { [id: number]: IFormProfile };

/**
 * Datová struktura jedné položky hlavního menu
 * @includeToDoc
 */
export interface IMenuItemData {
    /** Unikátní identifikátor položky menu */
    readonly id: number;

    /** Typ položky menu (formulář/složka) */
    readonly type: MenuItemType;

    /** Lokalizační klíč titulku položky */
    readonly locKey: string;

    /** Znak udávající kód ikony položky (nepovinný) */
    readonly icon?: IconCode;

    /** Kód formuláře otevíraného z položky (nepovinný) */
    readonly formCode?: string;

    /** Typ formuláře, který tato položka otevírá (nepovinný) */
    readonly openMode?: FormOpenMode;

    /** Kód vytvářející alias pro položku menu (nepovinný) */
    readonly code?: string;

    /** Identifikátor rodičovské položky, 0 u kořenové položky */
    readonly parent: number;

    /** Seřazený seznam identifikátorů položek potomků (nepovinný) */
    readonly children?: ReadonlyArray<number>;

    /** Seznam identifikátorů profilů (nepovinný) */
    readonly profiles?: ReadonlyArray<number>;
    /** Seznam rolí, které umožnily načtení položky menu (nepovinný) */
    readonly roles?: readonly string[];
}

/**
 * Datová struktura popisující uživatelsky vytvořený a uložený profil formuláře
 * @includeToDoc
 */
export interface IFormProfile {
    /** Unikátní identifikátor profilu */
    readonly id: number;
    /** Kód formuláře, ke kterému profil patří */
    readonly formCode: string;
    /** Název profilu */
    readonly title: string;
    /** Příznak jestli se má zobrazovat v menu*/
    readonly showInNavigationTree: boolean;
}

/**
 * Typ položky menu
 * @includeToDoc
 */
export enum MenuItemType {
    form = "form",
    folder = "folder",
    combined = "combined",
}

/**
 * Způsob otevření formuláře z menu
 * @includeToDoc
 */
export enum FormOpenMode {
    form = "form",
    paramForm = "paramForm",
    paramFormMethod = "paramFormMethod",
    paramFormReport = "paramFormReport",
    paramConditionForm = "paramConditionForm",
}

/**
 * Volitelná data využívaná při otevírání formuláře.
 */
export type OpenFormData = {
    /** Podmínka pro čtení dat. Připojuje se k podmínce hlavního framu. */
    readonly condition?: string;
    /** Identifikátor záznamu formuláře. */
    /** Id záznamu, který se má vybrat v hlavním framu */
    readonly selectedId?: string;
    /** Identifikátor profilu formuláře. */
    readonly profileId?: number;
    /** Způsob otevření formuláře. */
    readonly openMode?: FormOpenMode;
    /** Identifikátor položky menu. */
    readonly menuItemId?: number;
    /** Příznak, zda je formulář otevírán z menu */
    readonly isFromMainMenu?: boolean;
    /** Příznak, zda má být na otevřeném formuláři viditelný pouze DetailFrame */
    readonly visibleOnlyDetail?: boolean;
    /** Zprávy s výsledkem operace, které se mají zobrazit po otevření formuláře */
    readonly resultMessages?: readonly IResultMessage[];
    /** Kontext otevíraného formuláře */
    readonly formContext?: IFormContext;
};

/** Kódy povolených kořenových formulářových položek. */
export type AllowedMenuItemCodes = readonly string[] | null;
